import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MyAELayout } from "../components/myae/Layout";
import { listAnalyses, deleteAnalysis } from "../lib/api";
import { toast } from "sonner";
import {
  FileText, Trash2, Loader2, ArrowUpRight, FilePlus2,
  ChevronDown, RefreshCw,
} from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const StatusPill = ({ status, locked }) => {
  const map = {
    complete: locked ? ["bg-amber-100 text-amber-800", "Feedback only"] : ["bg-emerald-100 text-emerald-800", "Complete"],
    processing: ["bg-indigo-100 text-indigo-700", "Processing"],
    error: ["bg-red-100 text-red-800", "Failed"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-600", status];
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
};

/**
 * Group analyses by assignment_id. Each group has a `latest` (most recent by
 * created_at) and an ordered `drafts` list (oldest → newest).
 */
function groupByAssignment(items) {
  const map = new Map();
  for (const a of items) {
    const key = a.assignment_id || a.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(a);
  }
  const groups = [];
  for (const [key, list] of map.entries()) {
    list.sort((x, y) => (x.version || 1) - (y.version || 1));
    const latest = [...list].sort((x, y) => new Date(y.created_at) - new Date(x.created_at))[0];
    groups.push({
      assignment_id: key,
      drafts: list,
      latest,
      count: list.length,
    });
  }
  // Order groups by latest activity
  groups.sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
  return groups;
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState({});   // assignment_id -> bool

  const refresh = () => listAnalyses().then(setItems).catch(() => setItems([]));
  useEffect(() => { refresh(); }, []);

  const groups = useMemo(() => (items ? groupByAssignment(items) : []), [items]);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!confirm("Delete this draft? Any other drafts of this assignment remain untouched.")) return;
    try { await deleteAnalysis(id); toast.success("Deleted."); refresh(); }
    catch (err) { toast.error("Could not delete."); }
  };

  const toggle = (e, key) => {
    e.stopPropagation();
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <MyAELayout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-10" data-testid="reports-page">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900">Reports</h1>
            <p className="text-zinc-500 mt-1">All your examiner reports in one place. Multi-draft assignments are grouped together.</p>
          </div>
          <button onClick={() => navigate("/new")} data-testid="reports-new-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition-all active:scale-[0.98]">
            <FilePlus2 size={17} /> New Analysis
          </button>
        </div>

        {items === null ? (
          <div className="flex items-center gap-2 text-zinc-500 py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-12 text-center" data-testid="reports-empty">
            <FileText size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="font-medium text-zinc-900">No reports yet</p>
            <p className="text-sm text-zinc-500 mt-1">Run your first analysis to see it here.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm divide-y divide-zinc-100 overflow-hidden" data-testid="reports-list">
            {groups.map((grp) => {
              const a = grp.latest;
              const g = a.report?.estimated_grade?.percentage;
              const clickable = a.status === "complete";
              const isMultiDraft = grp.count > 1;
              const isExpanded = !!expanded[grp.assignment_id];
              return (
                <div key={grp.assignment_id} data-testid={`report-group-${grp.assignment_id}`}>
                  <div
                    onClick={() => clickable && navigate(`/report/${a.id}`)}
                    className={`flex items-center gap-4 px-5 py-4 transition-colors ${clickable ? "hover:bg-zinc-50 cursor-pointer" : ""}`}
                    data-testid={`report-row-${a.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                      {isMultiDraft ? <RefreshCw size={18} className="text-indigo-600" /> : <FileText size={18} className="text-indigo-600" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-zinc-900 truncate">{a.title || a.question || a.filename || "Untitled"}</p>
                        {isMultiDraft && (
                          <span className="text-[10px] font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`draft-count-${grp.assignment_id}`}>
                            {grp.count} drafts
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {isMultiDraft ? "Latest: " : ""}{fmtDate(a.created_at)} · {a.word_count || 0} words
                      </p>
                    </div>
                    <div className="hidden sm:block text-right shrink-0">
                      <p className="overline text-zinc-400">{isMultiDraft ? "Latest grade" : "Grade"}</p>
                      <p className="font-display text-xl font-semibold text-zinc-900 tabular-nums">{a.report?.grade_locked ? "—" : (typeof g === "number" ? `${g}%` : "—")}</p>
                    </div>
                    <StatusPill status={a.status} locked={a.report?.grade_locked} />
                    <div className="flex items-center gap-1 shrink-0">
                      {isMultiDraft && (
                        <button
                          onClick={(e) => toggle(e, grp.assignment_id)}
                          data-testid={`report-group-toggle-${grp.assignment_id}`}
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors"
                          aria-label={isExpanded ? "Collapse drafts" : "Expand drafts"}
                        >
                          <ChevronDown size={16} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                      )}
                      {!isMultiDraft && clickable && <ArrowUpRight size={18} className="text-zinc-400" />}
                      <button onClick={(e) => remove(e, a.id)} data-testid={`report-delete-${a.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors" title="Delete latest draft">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Draft sub-rows when expanded */}
                  {isMultiDraft && isExpanded && (
                    <div className="bg-zinc-50 border-t border-zinc-100" data-testid={`report-group-drafts-${grp.assignment_id}`}>
                      {grp.drafts.slice().reverse().map((d) => {
                        const dg = d.report?.estimated_grade?.percentage;
                        const isLatest = d.id === a.id;
                        const dClickable = d.status === "complete";
                        return (
                          <div
                            key={d.id}
                            onClick={() => dClickable && navigate(`/report/${d.id}`)}
                            className={`flex items-center gap-4 px-5 py-3 pl-14 border-t border-zinc-100 first:border-t-0 transition-colors ${dClickable ? "hover:bg-white cursor-pointer" : ""}`}
                            data-testid={`draft-row-${d.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-zinc-800">
                                Draft {d.version || 1}{isLatest && <span className="text-[10px] font-semibold text-indigo-600 ml-1.5">(Current)</span>}
                                <span className="text-xs text-zinc-500 font-mono ml-2">{fmtDate(d.created_at)}</span>
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-mono text-sm font-semibold text-zinc-900 tabular-nums">{d.report?.grade_locked ? "—" : (typeof dg === "number" ? `${dg}%` : "—")}</p>
                            </div>
                            <StatusPill status={d.status} locked={d.report?.grade_locked} />
                            <button onClick={(e) => remove(e, d.id)} data-testid={`draft-delete-${d.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MyAELayout>
  );
}
